import {
  SearchResponse,
  SearchResponseFacetCountSchema,
} from 'typesense/lib/Typesense/Documents';
import client, { unCacheTSClient } from './client';
import TS_CONFIG from './config';
import { ITSProduct } from './types/product';
import {
  ITSFilterOption,
  ITSProductQueryResponse,
  ITSTaxonomy,
  ITSTaxonomyFilterOptions,
  ITSTaxonomyProductQueryVars,
} from './types/taxonomy';
import { IFilterOptionData, TaxonomyPathsParams } from '../types/taxonomy';
import { isEmpty } from 'lodash';
import { getEndpointUrl } from '../helper';
import { Schema } from '../types/shema';
import DateHelper from '../DateHelper';
import getImageUrl from '../getImageUrl';
import { PRODUCT_PER_PAGE } from '../constants/product';

export const PER_PAGE_FIRST = 17;
export const PER_PAGE_REST = 18;

const totalPageCount = (totalResult: number) => {
  return Math.ceil((totalResult - PER_PAGE_FIRST) / PER_PAGE_REST + 1);
};

const sortOptions = [
  {
    label: 'Most Popular',
    value: 'totalSales:desc',
  },
  {
    label: 'Price Low to High',
    value: 'price.AUD:asc',
  },
  {
    label: 'Price High to Low',
    value: 'price.AUD:desc',
  },
  {
    label: 'On sale',
    value: 'salePrice.AUD:asc',
  },
  {
    label: 'Sort by title (A-Z)',
    value: 'name:asc',
  },
  {
    label: 'Sort by title (Z-A)',
    value: 'name:desc',
  },
  {
    label: 'Newest Gifts',
    value: 'createdAt:desc',
  },
];

const priceFilterOptions = {
  Low: {
    label: '$0 - $100',
    value: 'price.AUD:>=0 && price.AUD:<=100',
  },
  Mid: {
    label: '$100 - $150',
    value: 'price.AUD:>=100 && price.AUD:<=150',
  },
  High: {
    label: '$150 - $1699',
    value: 'price.AUD:>=150',
  },
};

const taxonomyUrlSlugToWpTaxonomySlug = (urlSlug: string) => {
  if ('product-category' == urlSlug) {
    return 'product_cat';
  }

  if ('product-tag' == urlSlug) {
    return 'product_tag';
  }

  return urlSlug;
};

const generateSearchParams = (queryVars: ITSTaxonomyProductQueryVars) => {
  // 	taxonomies.type:=[`favourite`] && taxonomies.slug:=[`gourmet-food-gift-baskets`] && taxonomies.nameAndType:=[`For Women|shop-by`,`Farewell|occasion`]
  //   facet_by=price(Low:[0, 100], Mid:[100, 150], High:[150, 10000])
  let filterByArr: string[] = [];
  if (
    typeof queryVars.taxonomySlug !== 'undefined' &&
    !isEmpty(queryVars.taxonomySlug)
  ) {
    const taxonomySlug = taxonomyUrlSlugToWpTaxonomySlug(
      queryVars.taxonomySlug,
    );
    filterByArr.push('taxonomies.type:=[`' + taxonomySlug + '`]');
  }

  if (
    typeof queryVars.termSlug !== 'undefined' &&
    !isEmpty(queryVars.termSlug)
  ) {
    filterByArr.push('taxonomies.slug:=[`' + queryVars.termSlug + '`]');
  }

  if (
    typeof queryVars.nameAndTypes !== 'undefined' &&
    !isEmpty(queryVars.nameAndTypes)
  ) {
    //	Sample query
    // 	taxonomies.nameAndType:=[`For Women|shop-by`,`Farewell|occasion`]
    const nameAndTypeStrArr = queryVars.nameAndTypes.join('`,`');
    filterByArr.push('taxonomies.nameAndType:=[`' + nameAndTypeStrArr + '`]');
  }

  /**
   * Price filter
   */
  if (
    queryVars.priceFilter !== 'undefined' &&
    !isEmpty(queryVars.priceFilter)
  ) {
    filterByArr.push(queryVars.priceFilter as string);
  }

  if (typeof queryVars.onSale !== 'undefined') {
    filterByArr.push('onSale:=[' + queryVars.onSale + ']');
  }

  let searchParameters = {
    collection: TS_CONFIG.collectionNames.product,
    q: `*`,
    query_by: 'slug',
    facet_by:
      'taxonomies.nameAndType,taxonomies.type,taxonomies.slug,price.AUD(Low:[0,100],Mid:[100,150],High:[150,10000]),status',
    per_page: queryVars.perPage,
    page: queryVars.page,
    max_facet_values: 100,
    sort_by: queryVars.sortBy, // Default sortby totalSales desc
    include_fields:
      'permalink,thumbnail,name,onSale,stockStatus,regularPrice,price,sku,createdAt,galleryImages,slug,shippingClass',
  };

  // Exclude data that is not for catalog in taxonomy pages results
  filterByArr.push(
    'taxonomies.nameAndType:!=[`exclude-from-catalog|product_visibility`]',
  );

  filterByArr.push('status:=[`publish`]');

  if (!isEmpty(filterByArr)) {
    searchParameters['filter_by'] = filterByArr.join(' && ');
  }

  if (queryVars.pinnedHits && !isEmpty(queryVars.pinnedHits)) {
    searchParameters['pinned_hits'] = queryVars.pinnedHits;
  }

  return searchParameters;
};

const getPageData = async (
  taxonomySlug: string,
  termSlug: string,
): Promise<ITSTaxonomy | null> => {
  let searchParameters = {
    q: `${termSlug}`,
    query_by: 'slug',
    filter_by: `type:=[${taxonomyUrlSlugToWpTaxonomySlug(taxonomySlug)}]`,
    sort_by: '_text_match:desc',
    facet_by: 'type',
  };

  const results = await client
    .collections(TS_CONFIG.collectionNames.taxonomy)
    .documents()
    .search(searchParameters);

  let found = null;
  if (typeof results.hits !== 'undefined' && results.hits[0]) {
    const tsTaxonomyData: ITSTaxonomy = JSON.parse(
      JSON.stringify(results.hits[0].document),
    );
    return tsTaxonomyData;
  }
  return found;
};

const getPopularProducts = async (
  taxonomySlug: string,
  termSlug: string,
): Promise<ITSProduct[]> => {
  let searchParameters = {
    q: `*`,
    query_by: 'slug',
    filter_by:
      'taxonomies.type:=[`' +
      taxonomyUrlSlugToWpTaxonomySlug(taxonomySlug) +
      '`] && taxonomies.slug:=[`' +
      termSlug +
      '`] && isFeatured:=[true]',
    facet_by:
      'taxonomies.type,isFeatured,taxonomies.nameAndType,taxonomies.slug',
    per_page: 2,
    sort_by: 'price.AUD:asc',
    // include_fields: 'permalink, slug, name',
  };
  const results = await unCacheTSClient
    .collections(TS_CONFIG.collectionNames.product)
    .documents()
    .search(searchParameters);

  let found: ITSProduct[] = [];
  if (typeof results.hits !== 'undefined' && results.hits) {
    results.hits.map((doc, i) => {
      const tsProductData: ITSProduct = JSON.parse(
        JSON.stringify(doc.document),
      );
      found.push(tsProductData);
    });
  }
  return found;
};

const getDefaultTsQueryVars = () => {
  const queryDefaults = {
    page: 1,
    perPage: PRODUCT_PER_PAGE,
    sortBy: 'totalSales:desc',
  };

  return queryDefaults;
};

const recompileTaxonomyFilterOptions = (
  facetData: SearchResponseFacetCountSchema<{}>[] | undefined,
) => {
  let taxonomyFilterOptions: ITSTaxonomyFilterOptions = {
    types: {
      label: 'Type',
      items: [],
    },
    favourites: {
      label: 'Favourite',
      items: [],
    },
    occasions: {
      label: 'Occasion',
      items: [],
    },
    suitableFor: {
      label: 'Suitable For',
      items: [],
    },
  };
  if (typeof facetData !== 'undefined' && typeof facetData[0] !== 'undefined') {
    let types: IFilterOptionData[] = [];
    let favourites: IFilterOptionData[] = [];
    let occasions: IFilterOptionData[] = [];
    let suitableFor: IFilterOptionData[] = [];
    facetData[0].counts.map((item) => {
      const nameAndTypeArr = item.value.split('|');
      const label = Object.values(nameAndTypeArr)[0];
      const type = Object.values(nameAndTypeArr)[1];

      let itemData = {
        count: item.count,
        value: item.value,
        label: label,
        type: type,
      };

      if ('product-type' === type) {
        types.push(itemData);
      }

      if ('favourite' === type) {
        favourites.push(itemData);
      }

      if ('occasion' === type) {
        occasions.push(itemData);
      }

      if ('shop-by' === type) {
        suitableFor.push(itemData);
      }
    });

    return {
      types: {
        label: 'Type',
        items: types,
      },
      favourites: {
        label: 'Favourite',
        items: favourites,
      },
      occasions: {
        label: 'Occasion',
        items: occasions,
      },
      suitableFor: {
        label: 'Suitable For',
        items: suitableFor,
      },
    };
  }

  return taxonomyFilterOptions;
};

const getPriceFilterOptions = (
  facetData: SearchResponseFacetCountSchema<{}>[] | undefined,
) => {
  if (typeof facetData !== 'undefined' && typeof facetData[3] !== 'undefined') {
    let priceOptions: IFilterOptionData[] = [];
    facetData[3].counts.map((item) => {
      const priceOption: IFilterOptionData = {
        label: priceFilterOptions[item.value].label,
        value: priceFilterOptions[item.value].value,
        count: item.count,
      };

      priceOptions.push(priceOption);
    });

    return {
      label: 'Price',
      items: priceOptions.reverse(),
    };
  }

  return {
    label: 'Price',
    items: [],
  };
};

const generateProductQueryResponse = (
  queryVars: ITSTaxonomyProductQueryVars,
  results: SearchResponse<{}>,
): ITSProductQueryResponse => {
  const taxonomyFilterOptions: ITSTaxonomyFilterOptions =
    recompileTaxonomyFilterOptions(results.facet_counts);

  const priceFilter: ITSFilterOption = getPriceFilterOptions(
    results.facet_counts,
  );

  let found: ITSProduct[] = [];
  if (typeof results.hits !== 'undefined' && results.hits) {
    results.hits.map((doc, i) => {
      const tsProductData: ITSProduct = JSON.parse(
        JSON.stringify(doc.document),
      );
      found.push(tsProductData);
    });
  }
  const totalPages = Math.ceil(results.found / queryVars.perPage);
  const hasMore = queryVars.page < totalPages;
  let nextPage = hasMore ? queryVars.page + 1 : 0;

  return {
    products: found,
    queryVars: queryVars,
    pageInfo: {
      totalFound: results.found,
      totalPages: totalPages,
      nextPage: nextPage,
      page: queryVars.page,
      perPage: queryVars.perPage,
    },
    taxonomyFilterOptions: taxonomyFilterOptions,
    priceFilter: priceFilter,
  };
};

const getProductDocument = () => {
  return client.collections(TS_CONFIG.collectionNames.product).documents();
};

const getProducts = async (
  queryVars: ITSTaxonomyProductQueryVars,
): Promise<ITSProductQueryResponse> => {
  let searchParameters = generateSearchParams(queryVars);
  console.log('searchParameters', searchParameters);
  const results = await unCacheTSClient
    .collections(TS_CONFIG.collectionNames.product)
    .documents()
    .search(searchParameters);
  return generateProductQueryResponse(queryVars, results);
};

const buildStaticPathParams = async (page: number) => {
  // Generate an array like below
  // [
  // 	{
  // 		params: {
  // 			taxonomyFrontendSlug:'',
  // 			taxonomyItemSlug: stripTrailingSlash(itemSlug)
  // 		}
  // 	}
  // ]

  let pathsData: TaxonomyPathsParams[] = [];

  let searchParameters = {
    q: '*',
    query_by: 'name',
    filter_by:
      'type:=[`product_cat`,`product_tag`,`occasion`,`product-type`,`favourite`,`shop-by`]',
    page: page,
    per_page: 250,
    include_fields: 'permalink',
  };

  const results = await client
    .collections(TS_CONFIG.collectionNames.taxonomy)
    .documents()
    .search(searchParameters);

  if (typeof results.hits !== 'undefined' && results.hits) {
    results.hits.map((doc, i) => {
      const permalink = doc.document['permalink'];
      //exclude link if it has ?product_type= on it
      if (!permalink.includes('?product_type=')) {
        let productEndpoint = getEndpointUrl(doc.document['permalink']);
        productEndpoint = productEndpoint.replace(/^\/|\/$/g, '');
        let taxSlug = productEndpoint.split('/');
        if (!isEmpty(taxSlug[0]) && !isEmpty(taxSlug[1])) {
          pathsData.push({
            params: {
              taxonomyFrontendSlug: taxSlug[0],
              taxonomyItemSlug: taxSlug[1],
            },
          });
        }
      }
    });
  }

  return pathsData;
};

const getSiteMapData = async (taxonomy: string, page = 1) => {
  let searchParameters = {
    q: '*',
    query_by: 'name',
    filter_by: 'type:=[`' + taxonomy + '`]',
    page: page,
    per_page: 50,
    sort_by: 'updatedAt:desc',
    include_fields: 'slug,type,permalink,bannerThumbnail,updatedAt',
  };

  const results = await client
    .collections(TS_CONFIG.collectionNames.taxonomy)
    .documents()
    .search(searchParameters);

  return results.hits;
};

const getSchemaItems = async (taxonomy: string) => {
  let page = 1;
  let continueFetching = true;

  let schemaItems: Schema[] = [];

  while (continueFetching) {
    const siteMapData = await getSiteMapData(taxonomy, page);

    if (typeof siteMapData === 'undefined' || isEmpty(siteMapData)) {
      continueFetching = false;
      continue;
    }

    siteMapData.map((doc, i) => {
      let lastMod = DateHelper.toDateTime(doc.document['updatedAt']);
      if (doc.document['type'] != 'product_type') {
        schemaItems.push({
          loc: `${process.env.NEXT_PUBLIC_FRONTEND_SITE_URL}${doc.document['permalink']}`,
          image: !isEmpty(doc.document['bannerThumbnail'])
            ? getImageUrl(doc.document['bannerThumbnail'])
            : '',
          lastMod: lastMod.toISOString(),
        });
      }
    });

    page += 1;
  }

  return schemaItems;
};

const TSTaxonomy = {
  getPageData: getPageData,
  getPopularProducts,
  getProducts,
  sortOptions,
  getDefaultTsQueryVars,
  generateSearchParams,
  generateProductQueryResponse,
  getProductDocument,
  taxonomyUrlSlugToWpTaxonomySlug,
  buildStaticPathParams,
  totalPageCount,
  getSiteMapData,
  getSchemaItems,
};

export default TSTaxonomy;
